import { howYouSendCard } from '../email-send'
import { currencySymbol, monthFirst } from '../round-config'
import { api, mutate } from '../api'
import { aheadWords, applySavedBusinessRow, askAheadDays, ASK_AHEAD_CHOICES, cleanWording, sampleValues, specFor, STANDARD_ASK_AHEAD_DAYS, WORDING, wordingTemplate, fillTemplate, MAX_WORDING_LENGTH, type BusinessSettings, type WordingKey } from '../business-settings'
import { routeGen } from '../route-state'
import { undoPatch } from '../undo'
import { clear, el, loadError } from '../ui'
import { sendAllOn } from '../send-all-on'

// More > Your messages (Liam, 8 Oct 2026: "there should be somewhere we change these messages ... users need easy
// ways to adjust these"). Every message Roundly writes for a customer, grouped as the owner meets them, each with
// its wording in a box, tags that fill themselves in, a live preview with a sample customer, and Back to standard.
// Saves into Your business (PATCH /api/business message_wording) with Undo. #/your-messages?m=<key> opens one.

interface Payload { settings: BusinessSettings; version: number }
let payload: Payload | null = null
let requestSequence = 0
const drafts = new Map<WordingKey, string>()
let openKey: WordingKey | null = null

function routeKey(): WordingKey | null {
  const q = window.location.hash.split('?')[1] ?? ''
  const m = new URLSearchParams(q).get('m')
  return m && WORDING.some((w) => w.key === m) ? (m as WordingKey) : null
}

function unknownTags(key: WordingKey, text: string): string[] {
  const known = new Set(specFor(key).tags.map((t) => t.tag))
  return [...new Set([...text.matchAll(/\{([^{}]+)\}/g)].map((m) => m[1]!).filter((t) => !known.has(t)))]
}

function card(key: WordingKey, data: Payload): HTMLElement {
  const spec = specFor(key)
  const own = wordingTemplate(key, data.settings) !== spec.standard
  const box = el('details', { class: 'wording-card', id: `wording-${key}` })
  box.open = openKey === key
  box.append(el('summary', {}, el('span', { class: 'wording-title' }, el('strong', {}, spec.title), el('small', {}, spec.when)),
    el('span', { class: `wording-badge${own ? ' wording-own' : ''}` }, own ? 'Your wording' : 'Standard')))
  box.addEventListener('toggle', () => { if (box.open) openKey = key; else if (openKey === key) openKey = null })
  const text = el('textarea', { 'aria-label': `Wording for ${spec.title}`, rows: '7', maxlength: String(MAX_WORDING_LENGTH) }) as HTMLTextAreaElement
  text.value = drafts.get(key) ?? wordingTemplate(key, data.settings)
  const tags = el('div', { class: 'wording-tags', role: 'group', 'aria-label': 'Tags that fill themselves in' })
  // Each tag's example in the round's own money, bank line and dates, as the preview reads (Step 2 round 6).
  const examples = sampleValues(key, null, currencySymbol(), monthFirst())
  for (const t of spec.tags) {
    const chip = el('button', { type: 'button', class: 'wording-tag', title: `${t.means}, e.g. ${(examples[t.tag] ?? t.sample).replace(/\n+/g, ' ').trim()}` }, `{${t.tag}}`)
    chip.addEventListener('click', () => {
      const at = text.selectionStart ?? text.value.length, end = text.selectionEnd ?? at
      text.value = `${text.value.slice(0, at)}{${t.tag}}${text.value.slice(end)}`
      text.focus(); const caret = at + t.tag.length + 2; text.setSelectionRange(caret, caret)
      text.dispatchEvent(new Event('input'))
    })
    tags.append(chip)
  }
  const preview = el('p', { class: 'wording-preview-text' })
  const warn = el('p', { class: 'form-error', role: 'alert' })
  const paint = (): void => {
    preview.textContent = fillTemplate(text.value.trim() ? text.value : spec.standard, sampleValues(key, data.settings, currencySymbol(), monthFirst()))
    const odd = unknownTags(key, text.value)
    warn.textContent = odd.length ? `Not a tag Roundly fills in: ${odd.map((t) => `{${t}}`).join(', ')}. It would be sent exactly as typed.` : ''
    save.disabled = text.value === wordingTemplate(key, data.settings)
  }
  text.addEventListener('input', () => { drafts.set(key, text.value); paint() })
  const save = el('button', { type: 'button', class: 'btn-primary' }, 'Save') as HTMLButtonElement
  const standard = el('button', { type: 'button', class: 'btn-secondary' }, 'Back to standard')
  standard.hidden = !own && text.value === spec.standard
  const cancel = el('button', { type: 'button', class: 'link-button' }, 'Cancel')
  const send = async (next: string): Promise<void> => {
    const before = data.settings.message_wording ?? null
    const wording = cleanWording({ ...(before ?? {}), [key]: next })
    save.disabled = true; standard.disabled = true
    const ack = await mutate({ label: `Message wording: ${spec.title}`, method: 'PATCH', path: '/api/business', body: { version: data.version, message_wording: wording } })
    standard.disabled = false
    if (ack.ok) {
      applySavedBusinessRow(ack.row)
      drafts.delete(key)
      undoPatch({ text: `Saved wording for ${spec.title}`, label: `Undo wording: ${spec.title}`, path: '/api/business', ack, version: data.version,
        body: { message_wording: before }, after: () => { void load() } })
      void load()
      return
    }
    paint()
    if (ack.status === 409) { warn.textContent = 'Your business was changed elsewhere. Reloading; your typing is kept.'; void load() }
    else if (ack.status >= 400 && ack.status < 500) warn.textContent = ack.error ?? 'That could not be saved.'
    else warn.textContent = 'Waiting for signal. It will save once the connection is back.'
  }
  save.addEventListener('click', () => void send(text.value))
  standard.addEventListener('click', () => { text.value = spec.standard; drafts.delete(key); void send(spec.standard) })
  cancel.addEventListener('click', () => { drafts.delete(key); text.value = wordingTemplate(key, data.settings); paint(); box.open = false })
  box.append(el('div', { class: 'wording-body' },
    el('label', { class: 'wording-field' }, el('span', {}, 'Wording'), text),
    el('p', { class: 'muted wording-help' }, 'Tap a tag to put it where the cursor is. Roundly fills it in for each customer, and leaves it out when there is nothing to say.'),
    tags,
    el('div', { class: 'wording-preview' }, el('span', { class: 'rebook-fact-label' }, 'How it reads, with a sample customer'), preview),
    warn,
    el('div', { class: 'wording-actions' }, save, standard, cancel)))
  paint()
  return box
}

// How far ahead to ask (Liam, 9 Oct 2026: "Maybe just a week but with a setting somwhere in more to chane this?"):
// one to four weeks, the standard one. It sets how long before their visit or due date people show in Messages under
// Coming up (and in the Messages count), and how far ahead Book a visit offers "Message them". Saves into Your business
// (PATCH /api/business ask_ahead_days, 0052) with Undo.
function aheadCard(data: Payload): HTMLElement {
  const now = askAheadDays(data.settings)
  const status = el('p', { class: 'form-error', role: 'alert' })
  const group = el('div', { class: 'ask-ahead-switch', role: 'group', 'aria-label': 'How far ahead to ask' })
  const pick = async (days: number, button: HTMLButtonElement): Promise<void> => {
    if (days === askAheadDays(data.settings)) return
    const before = askAheadDays(data.settings)
    for (const b of group.querySelectorAll<HTMLButtonElement>('button')) b.setAttribute('aria-pressed', String(b === button))
    group.setAttribute('aria-busy', 'true')
    status.textContent = ''
    const ack = await mutate({ label: `How far ahead to ask: ${aheadWords(days)}`, method: 'PATCH', path: '/api/business', body: { version: data.version, ask_ahead_days: days } })
    group.removeAttribute('aria-busy')
    if (ack.ok) {
      applySavedBusinessRow(ack.row)
      undoPatch({ text: `Asking ${aheadWords(days)} ahead`, label: `Undo how far ahead to ask: ${aheadWords(days)}`, path: '/api/business', ack, version: data.version,
        body: { ask_ahead_days: before }, onAck: (back) => { if (back.ok) applySavedBusinessRow(back.row) }, after: () => { void load() } })
      void load()
      return
    }
    for (const b of group.querySelectorAll<HTMLButtonElement>('button')) b.setAttribute('aria-pressed', String(Number(b.dataset.days) === before))
    if (ack.status === 409) { status.textContent = 'Your business was changed elsewhere. Reloading; pick again.'; void load() }
    else if (ack.status >= 400 && ack.status < 500) status.textContent = ack.error ?? 'That could not be saved.'
    else status.textContent = 'Waiting for signal. It will save once the connection is back.'
  }
  for (const days of ASK_AHEAD_CHOICES) {
    const b = el('button', { type: 'button', 'aria-pressed': String(days === now), 'data-days': String(days) }, aheadWords(days)) as HTMLButtonElement
    b.addEventListener('click', () => void pick(days, b))
    group.append(b)
  }
  return el('section', { class: 'ask-ahead-card', 'aria-labelledby': 'ask-ahead-title' },
    el('h3', { id: 'ask-ahead-title' }, 'How far ahead to ask'),
    el('p', { class: 'muted' }, 'People show in Messages under "Coming up" this long before their visit or due date. Book a visit offers "Message them" for a visit booked within it.'),
    group,
    el('p', { class: 'muted ask-ahead-note' }, `${aheadWords(STANDARD_ASK_AHEAD_DAYS)} is the standard. Undo after a change.`),
    status)
}

// Send all payment texts (0093; Liam, 27 Oct 2026: keep the review link as an option): payment texts from Send all are
// payment only as standard; this adds the Google reviews link from Your business above the sign-off. Owner only, off
// to start, shown once texting is connected; offered only once a reviews link is saved.
function reviewsInTextsCard(data: Payload): HTMLElement {
  const on = data.settings.reviews_in_texts === true
  const link = data.settings.reviews_link?.trim() ?? ''
  const status = el('p', { class: 'form-error', role: 'alert' })
  const sw = el('button', { type: 'button', role: 'switch', class: `mb-switch feature-switch${on ? ' mb-switch-on' : ''}`, 'aria-checked': String(on), 'aria-label': 'Add my review link to payment texts' },
    el('span', { class: 'mb-switch-knob' })) as HTMLButtonElement
  sw.disabled = !link && !on
  sw.addEventListener('click', async () => {
    sw.disabled = true
    const ack = await mutate({ label: `Review link in payment texts: ${on ? 'off' : 'on'}`, method: 'PATCH', path: '/api/business', body: { version: data.version, reviews_in_texts: !on } })
    if (ack.ok) {
      applySavedBusinessRow(ack.row)
      undoPatch({ text: on ? 'Review link taken out of payment texts' : 'Review link added to payment texts', label: 'Undo review link in payment texts', path: '/api/business', ack, version: data.version,
        body: { reviews_in_texts: on }, onAck: (back) => { if (back.ok) applySavedBusinessRow(back.row) }, after: () => { void load() } })
      void load()
      return
    }
    sw.disabled = false
    status.textContent = ack.status === 409 ? 'Your business was changed elsewhere. Reloading; try again.' : ack.error ?? 'Waiting for signal. It will save once the connection is back.'
    if (ack.status === 409) void load()
  })
  return el('section', { class: 'ask-ahead-card reviews-texts-card', 'aria-labelledby': 'reviews-texts-title' },
    el('div', { class: 'reviews-texts-top' }, el('h3', { id: 'reviews-texts-title' }, 'Add my review link to payment texts'), sw),
    el('p', { class: 'muted' }, 'Only for texts from Send all. A review request in a text can count as marketing under UK rules, so only switch this on if your customers have agreed to hear from you.'),
    el('p', { class: 'muted' }, link ? `Your Google reviews link: ${link}` : 'Add your Google reviews link in Your business first.'),
    status)
}

function render(data: Payload, root: HTMLElement): void {
  clear(root)
  const page = el('div', { class: 'wording-page' })
  page.append(el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'Your messages'),
    el('p', { class: 'muted' }, 'The words Roundly writes for your customers, and when Roundly asks. Change any of them; it is still you who sends each one.'))))
  page.append(howYouSendCard(), aheadCard(data))
  for (const group of ['Messages', 'Owed', 'Enquiries'] as const) {
    const section = el('section', { class: 'wording-group', 'aria-labelledby': `wording-${group}` }, el('h3', { id: `wording-${group}` }, group === 'Owed' ? 'Owed: payment reminders' : group))
    if (group === 'Owed' && sendAllOn()) section.append(reviewsInTextsCard(data))
    for (const spec of WORDING.filter((w) => w.group === group)) section.append(card(spec.key, data))
    page.append(section)
  }
  root.append(page)
  if (openKey) root.querySelector<HTMLElement>(`#wording-${openKey}`)?.scrollIntoView({ block: 'start' })
  // Arrived from "Change wording": the page's own return to the top (route-presentation.ts) runs on the next frame and
  // undid the scroll above, so the chosen message sat a screen or more below (bug hunt 8 Oct 2026, owed-today #9).
  // Scrolled again once that has run, for each draw until the fresh read is in.
  // Clear of the top bar, which stays over the page on a phone.
  if (openKey && arrivedFor === openKey) {
    const key = openKey
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const card = document.querySelector<HTMLElement>(`#wording-${key}`)
      if (!card) return
      const bar = document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect().bottom ?? 0
      window.scrollTo({ top: Math.max(0, window.scrollY + card.getBoundingClientRect().top - Math.max(0, bar) - 12), behavior: 'auto' })
    }))
  }
}

async function load(): Promise<void> {
  const gen = routeGen
  const sequence = ++requestSequence
  const root = document.querySelector<HTMLElement>('#screen')!
  try {
    const data = await api<Payload>('/api/business')
    if (gen !== routeGen || sequence !== requestSequence) return
    payload = data
    render(data, root)
    arrivedFor = null
  } catch (err) {
    if (gen !== routeGen || sequence !== requestSequence) return
    clear(root)
    loadError(root, (err as Error).message, () => void load())
  }
}

// The message a "Change wording" link opened this page on, until the fresh read has drawn it.
let arrivedFor: WordingKey | null = null

export function mountYourMessages(): void {
  arrivedFor = routeKey()
  openKey = routeKey() ?? openKey
  if (payload) render(payload, document.querySelector<HTMLElement>('#screen')!)
  void load()
}
